import React, { useCallback, useEffect, useState } from "react";
import { Button, Spin, Table } from "antd";
import { CreditCardOutlined, GiftOutlined, PercentageOutlined, PlusOutlined } from "@ant-design/icons";

import * as api from "../controller";
import OfferEditorModal from "./OfferEditorModal";
import OfferList from "./OfferList";
import { BillingSection, PlanStatePill } from "./OfficeBillingPanel";
import SalesTaxCard from "./SalesTaxCard";
import { chargeView, planLine, shortDate } from "./planState";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

/**
 * CRM practice drawer → Users & Billing: each office's plan state (a practice
 * pays per office, each on its own plan), the practice's sales tax and the
 * practice-wide offers, which every office of the practice can claim. Office-only
 * offers and tax, the trial cap and complimentary access are on each office's own
 * drawer.
 */
export default function PracticeBillingPanel({ practiceId, practiceName, onOpenOffice }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    const r = await api.getPracticeBilling(practiceId);
    setFailed(!ok(r));
    if (ok(r)) setData(r.json);
  }, [practiceId]);
  useEffect(() => { setData(null); load(); }, [load]);

  if (failed && !data) {
    return (
      <p className="crm-sub" style={{ margin: "16px 24px" }}>
        Couldn&rsquo;t load billing for this practice.{" "}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
      </p>
    );
  }
  if (!data) return <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>;

  const offices = data.offices || [];
  const names = Object.fromEntries(offices.map((o) => [o.office_id, o.office_name]));

  return (
    <>
      <BillingSection icon={<CreditCardOutlined />} title="Plans by office" sub="Each office has its own plan">
        <Table
          rowKey="office_id"
          size="small"
          pagination={false}
          dataSource={offices}
          onRow={(row) => (onOpenOffice ? { onClick: () => onOpenOffice(row.office_id, "users"), style: { cursor: "pointer" } } : {})}
          locale={{ emptyText: <div className="crm-empty">No offices yet.</div> }}
          columns={[
            {
              title: "Office", key: "office",
              render: (_, r) => (
                <>
                  <div className="ub-office-name">{r.office_name}</div>
                  {r.complimentary_access && r.state !== "exempt" ? (
                    <div className="ub-office-sub">Complimentary access on</div>
                  ) : null}
                </>
              ),
            },
            { title: "Status", key: "state", width: 150, render: (_, r) => <PlanStatePill row={r} /> },
            {
              title: "Plan", key: "plan",
              render: (_, r) => (r.plan ? planLine(r.plan)
                : r.trial && r.trial.active ? `Trial · ${r.trial.used}/${r.trial.quota}` : "—"),
            },
            {
              title: "Renews", key: "renews", width: 120,
              render: (_, r) => (r.plan ? shortDate(r.plan.next_billing) : "—"),
            },
            {
              title: "Each charge", key: "charge", width: 130, align: "right",
              render: (_, r) => {
                const c = r.plan && chargeView(r.plan.charge);
                if (!c) return "—";
                return (
                  <span title={c.note || undefined} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {c.total}{c.note ? " *" : ""}
                  </span>
                );
              },
            },
          ]}
        />
        {offices.some((o) => o.plan && chargeView(o.plan.charge) && chargeView(o.plan.charge).note) ? (
          <div className="leadset-hint" style={{ padding: "6px 8px 2px" }}>* Bought with a special offer.</div>
        ) : null}
      </BillingSection>

      <BillingSection icon={<PercentageOutlined />} title="Sales tax" sub="Each office's province rate unless set here">
        <SalesTaxCard
          practiceId={practiceId}
          summary={data.sales_tax}
          onChanged={(salesTax) => setData((d) => ({ ...d, sales_tax: salesTax }))}
        />
      </BillingSection>

      <BillingSection
        icon={<GiftOutlined />}
        title="Practice-wide offers"
        sub="Every office can claim these"
        extra={(
          <Button size="small" type="primary" ghost icon={<PlusOutlined />} onClick={() => setEditor({})}>
            New offer
          </Button>
        )}
      >
        <OfferList
          offers={data.offers}
          officeNames={names}
          practiceName={practiceName}
          onEdit={(offer) => setEditor({ offer })}
          onChanged={load}
          emptyText="No practice-wide offers. Office-only offers are on each office's Users & Billing tab."
        />
      </BillingSection>

      <OfferEditorModal
        open={!!editor}
        offer={editor && editor.offer}
        scope={{ practiceId, name: practiceName }}
        onClose={() => setEditor(null)}
        onSaved={load}
      />
    </>
  );
}
